import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { CircleCheck, CircleX, Send, ThumbsDown } from 'lucide-react';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@bliks-io/ui';
import type { PartnerContact, PartnerLead } from '@bliks-io/contracts';
import { formatDate } from '@/lib/formatters';
import { useHubspotLabel } from '@/pages/partner/hubspot-labels';

interface ContactOutcomeCellProps {
  contact: PartnerContact;
  /** The HubSpot lead with this contact's email, if there is one. */
  matchedLead: PartnerLead | undefined;
  isSalesActivated: boolean;
  onSubmitLead: (contact: PartnerContact) => void;
  onNoInterest: (contact: PartnerContact) => void;
}

/**
 * How the rep is done with a contact. Open contacts offer both ways to close
 * them; a closed one says how and when: green for a lead, red for no interest.
 */
export function ContactOutcomeCell({
  contact,
  matchedLead,
  isSalesActivated,
  onSubmitLead,
  onNoInterest
}: ContactOutcomeCellProps) {
  const { t } = useTranslation();
  const label = useHubspotLabel();
  const date = contact.outcomeAt ? formatDate(contact.outcomeAt) : '';

  if (matchedLead) {
    return (
      <Link
        to="/partner/leads"
        className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-positive hover:underline"
      >
        <CircleCheck className="h-4 w-4" />
        {matchedLead.lifecycleStage
          ? t('partner.contacts.outcome.leadInPhase', {
              phase: label('lifecycleStage', matchedLead.lifecycleStage)
            })
          : t('partner.contacts.outcome.leadInHubspot')}
      </Link>
    );
  }

  if (contact.outcome === 'lead') {
    return (
      <Link
        to="/partner/leads"
        className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-positive hover:underline"
      >
        <CircleCheck className="h-4 w-4" />
        {t('partner.contacts.outcome.lead', { date })}
      </Link>
    );
  }

  if (contact.outcome === 'noInterest') {
    const label = (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-danger">
        <CircleX className="h-4 w-4" />
        {t('partner.contacts.outcome.noInterest', { date })}
      </span>
    );
    if (!contact.note) return label;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{label}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs whitespace-pre-line">{contact.note}</TooltipContent>
      </Tooltip>
    );
  }

  const leadButton = (
    <Button
      variant="outline"
      size="small"
      disabled={!isSalesActivated}
      onClick={() => onSubmitLead(contact)}
    >
      <Send className="mr-2 h-4 w-4" />
      {t('partner.contacts.submitAsLead')}
    </Button>
  );

  return (
    <div className="flex items-center gap-2">
      {isSalesActivated ? (
        leadButton
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* A disabled button fires no pointer events, so the tooltip needs a wrapper. */}
            <span>{leadButton}</span>
          </TooltipTrigger>
          <TooltipContent>{t('partner.newLead.salesNotActivatedHint')}</TooltipContent>
        </Tooltip>
      )}
      <Button variant="ghost" size="small" onClick={() => onNoInterest(contact)}>
        <ThumbsDown className="mr-2 h-4 w-4" />
        {t('partner.contacts.noInterest.button')}
      </Button>
    </div>
  );
}
